import React, { useEffect, useMemo, useRef, useState } from "react";

const W = 980;
const H = 580;

const CDN_D3 = "https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js";
const CDN_TOPOJSON = "https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js";
const WORLD_ATLAS = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const SCRIPT_PROMISES = new Map();

function loadScriptOnce(src) {
  if (SCRIPT_PROMISES.has(src)) return SCRIPT_PROMISES.get(src);
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      const isReady =
        existing.dataset.loaded === "1" ||
        (src.includes("d3") ? !!window.d3 : src.includes("topojson") ? !!window.topojson : false);
      if (isReady) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = () => {
      s.dataset.loaded = "1";
      resolve();
    };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  SCRIPT_PROMISES.set(src, p);
  return p;
}

async function ensureD3Topo() {
  await loadScriptOnce(CDN_D3);
  await loadScriptOnce(CDN_TOPOJSON);
  if (!window.d3 || !window.topojson) throw new Error("D3/TopoJSON not available");
  return { d3: window.d3, topojson: window.topojson };
}

// EU ports we display. Keys match landDelivery.json `seaPorts[]`.
// `dir` hints label placement so the dense North-Sea cluster stays readable.
export const EU_PORTS = [
  { key: "rotterdam_nl", label: "Rotterdam, NL", lng: 4.4792, lat: 51.9225, cc: "nl", dir: "top" },
  { key: "bremerhaven_de", label: "Bremerhaven, DE", lng: 8.5832, lat: 53.5396, cc: "de", dir: "top" },
  { key: "hamburg_de", label: "Hamburg, DE", lng: 9.9937, lat: 53.5511, cc: "de", dir: "right" },
  { key: "antwerp_be", label: "Antwerp, BE", lng: 4.4025, lat: 51.2194, cc: "be", dir: "bottom" },
  { key: "felixstowe_uk", label: "Felixstowe, UK", lng: 1.3134, lat: 51.964, cc: "gb", dir: "left" },
  { key: "le_havre_fr", label: "Le Havre, FR", lng: 0.1079, lat: 49.4944, cc: "fr", dir: "bottom" },
  { key: "klaipeda_lt", label: "Klaipeda, LT", lng: 21.144, lat: 55.7033, cc: "lt", dir: "right" },
  { key: "gdynia_pl", label: "Gdynia, PL", lng: 18.531, lat: 54.5189, cc: "pl", dir: "top" },
  { key: "poti_ge", label: "Poti, GE", lng: 41.642, lat: 42.146, cc: "ge", dir: "top" },
];

// world-atlas uses ISO 3166-1 numeric ids.
const EUROPE = new Set([
  40, 56, 70, 100, 112, 191, 196, 203, 208, 233, 246, 250, 268, 276, 300, 348, 352, 372, 380, 428, 440, 442,
  470, 492, 498, 528, 578, 616, 620, 642, 643, 703, 705, 724, 752, 756, 804, 807, 826,
]);

const HIGHLIGHT = new Set([528, 276, 56, 826, 250, 440, 616]); // NL, DE, BE, GB, FR, LT, PL

// Country id (ISO 3166-1 numeric) → flag + two flag colours for the hover
// tooltip. Russia (643) is intentionally omitted — it is not interactive.
const EU_COUNTRY = {
  40: { iso2: "AT", name: "Austria", c1: "#ed2939", c2: "#ffffff" },
  56: { iso2: "BE", name: "Belgium", c1: "#2d2926", c2: "#fdda24" },
  70: { iso2: "BA", name: "Bosnia & Herzegovina", c1: "#002395", c2: "#fecb00" },
  100: { iso2: "BG", name: "Bulgaria", c1: "#00966e", c2: "#d62612" },
  112: { iso2: "BY", name: "Belarus", c1: "#d22730", c2: "#4aa657" },
  191: { iso2: "HR", name: "Croatia", c1: "#ff0000", c2: "#171796" },
  196: { iso2: "CY", name: "Cyprus", c1: "#d57800", c2: "#ffffff" },
  203: { iso2: "CZ", name: "Czechia", c1: "#11457e", c2: "#d7141a" },
  208: { iso2: "DK", name: "Denmark", c1: "#c8102e", c2: "#ffffff" },
  233: { iso2: "EE", name: "Estonia", c1: "#0072ce", c2: "#000000" },
  246: { iso2: "FI", name: "Finland", c1: "#002f6c", c2: "#ffffff" },
  250: { iso2: "FR", name: "France", c1: "#0055a4", c2: "#ef4135" },
  268: { iso2: "GE", name: "Georgia", c1: "#ff0000", c2: "#ffffff" },
  276: { iso2: "DE", name: "Germany", c1: "#dd0000", c2: "#ffce00" },
  300: { iso2: "GR", name: "Greece", c1: "#0d5eaf", c2: "#ffffff" },
  348: { iso2: "HU", name: "Hungary", c1: "#cd2a3e", c2: "#436f4d" },
  352: { iso2: "IS", name: "Iceland", c1: "#02529c", c2: "#dc1e35" },
  372: { iso2: "IE", name: "Ireland", c1: "#169b62", c2: "#ff883e" },
  380: { iso2: "IT", name: "Italy", c1: "#008c45", c2: "#cd212a" },
  428: { iso2: "LV", name: "Latvia", c1: "#9e3039", c2: "#ffffff" },
  440: { iso2: "LT", name: "Lithuania", c1: "#fdb913", c2: "#006a44" },
  442: { iso2: "LU", name: "Luxembourg", c1: "#ed2939", c2: "#00a1de" },
  470: { iso2: "MT", name: "Malta", c1: "#cf142b", c2: "#ffffff" },
  498: { iso2: "MD", name: "Moldova", c1: "#0046ae", c2: "#ffd200" },
  528: { iso2: "NL", name: "Netherlands", c1: "#ae1c28", c2: "#21468b" },
  578: { iso2: "NO", name: "Norway", c1: "#ba0c2f", c2: "#00205b" },
  616: { iso2: "PL", name: "Poland", c1: "#dc143c", c2: "#ffffff" },
  620: { iso2: "PT", name: "Portugal", c1: "#006600", c2: "#ff0000" },
  642: { iso2: "RO", name: "Romania", c1: "#002b7f", c2: "#fcd116" },
  703: { iso2: "SK", name: "Slovakia", c1: "#0b4ea2", c2: "#ee1c25" },
  705: { iso2: "SI", name: "Slovenia", c1: "#005da4", c2: "#ed1c24" },
  724: { iso2: "ES", name: "Spain", c1: "#aa151b", c2: "#f1bf00" },
  752: { iso2: "SE", name: "Sweden", c1: "#006aa7", c2: "#fecc00" },
  756: { iso2: "CH", name: "Switzerland", c1: "#d52b1e", c2: "#ffffff" },
  804: { iso2: "UA", name: "Ukraine", c1: "#0057b7", c2: "#ffd700" },
  807: { iso2: "MK", name: "North Macedonia", c1: "#d20000", c2: "#ffe600" },
  826: { iso2: "GB", name: "United Kingdom", c1: "#012169", c2: "#c8102e" },
};

function flagEmoji(iso2) {
  return String(iso2).toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Ports located in a given country (matched by the port's `cc`).
function portsForCountry(iso2) {
  const cc = String(iso2).toLowerCase();
  return EU_PORTS.filter((p) => p.cc === cc);
}

const ACCENT = "#0284c7";
const ACCENT_DARK = "#0369a1";

// The Natural Earth (world-atlas) dataset assigns the Crimea peninsula to
// Russia's polygon. We correct this to the internationally recognised borders:
// at the TopoJSON arc level we cut Crimea's arcs out of Russia and graft them
// onto Ukraine. Working on arcs (not coordinates) lets topojson.merge() later
// dissolve the Crimea↔mainland seam so Ukraine draws as one clean shape.
const CRIMEA_POINT = [34.1, 45.0];
function fixCrimeaBorders(world, topojson, d3) {
  const geoms = world?.objects?.countries?.geometries;
  if (!geoms) return;
  const ru = geoms.find((g) => +g.id === 643);
  const ua = geoms.find((g) => +g.id === 804);
  if (!ru || !ua || ru.type !== "MultiPolygon") return;

  // Locate the Crimea sub-polygon inside Russia's geometry.
  const ruFeat = topojson.feature(world, ru);
  const ruPolys = ruFeat.geometry.type === "MultiPolygon"
    ? ruFeat.geometry.coordinates
    : [ruFeat.geometry.coordinates];
  let idx = -1;
  for (let i = 0; i < ruPolys.length; i++) {
    const test = { type: "Feature", geometry: { type: "Polygon", coordinates: ruPolys[i] } };
    if (d3.geoContains(test, CRIMEA_POINT)) { idx = i; break; }
  }
  if (idx < 0) return;

  // Move Crimea's arcs from Russia to Ukraine.
  const crimeaArcs = ru.arcs.splice(idx, 1)[0];
  if (ru.arcs.length === 1) { ru.type = "Polygon"; ru.arcs = ru.arcs[0]; }
  if (ua.type === "Polygon") { ua.type = "MultiPolygon"; ua.arcs = [ua.arcs, crimeaArcs]; }
  else { ua.arcs.push(crimeaArcs); }
}

export default function EuPortsMap({ selectedPort = "", onSelectPort }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const ttRef = useRef(null);
  const [status, setStatus] = useState("loading");

  const ports = useMemo(() => EU_PORTS, []);

  // Port chooser opened by clicking a country territory (when it has ≥2 ports).
  const [chooser, setChooser] = useState(null);
  const chooserOpenRef = useRef(null);
  useEffect(() => { chooserOpenRef.current = setChooser; }, []);

  const onSelectPortRef = useRef(onSelectPort);
  useEffect(() => { onSelectPortRef.current = onSelectPort; }, [onSelectPort]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    async function run() {
      try {
        setStatus("loading");
        const { d3, topojson } = await ensureD3Topo();
        if (cancelled || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        svg.attr("viewBox", `0 0 ${W} ${H}`);

        // ── DEFS: sea gradient, land sheen, shadows ─────────────────────
        const defs = svg.append("defs");

        const seaGrad = defs.append("linearGradient")
          .attr("id", "eu-sea").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        seaGrad.append("stop").attr("offset", "0%").attr("stop-color", "#eaf3fb");
        seaGrad.append("stop").attr("offset", "100%").attr("stop-color", "#dceaf6");

        const landGrad = defs.append("linearGradient")
          .attr("id", "eu-land").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        landGrad.append("stop").attr("offset", "0%").attr("stop-color", "#fbfdff");
        landGrad.append("stop").attr("offset", "100%").attr("stop-color", "#eef3f9");

        const hiGrad = defs.append("linearGradient")
          .attr("id", "eu-land-hi").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        hiGrad.append("stop").attr("offset", "0%").attr("stop-color", "#e0f2fe");
        hiGrad.append("stop").attr("offset", "100%").attr("stop-color", "#cbe6fb");

        // Clip path (updated per hovered country) used to mask the flag image to
        // the exact country territory, and a glow filter for the hover outline.
        const flagClip = defs.append("clipPath").attr("id", "eu-flag-clip");
        const flagClipPath = flagClip.append("path");
        const hoverGlow = defs.append("filter")
          .attr("id", "eu-hover-glow").attr("x", "-40%").attr("y", "-40%").attr("width", "180%").attr("height", "180%");
        hoverGlow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "0").attr("stdDeviation", "3.5")
          .attr("flood-color", "#38bdf8").attr("flood-opacity", "0.9");

        const landShadow = defs.append("filter")
          .attr("id", "eu-land-shadow").attr("x", "-20%").attr("y", "-20%").attr("width", "140%").attr("height", "140%");
        landShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "6").attr("stdDeviation", "9")
          .attr("flood-color", "#1e3a5f").attr("flood-opacity", "0.16");

        const pinShadow = defs.append("filter")
          .attr("id", "eu-pin-shadow").attr("x", "-60%").attr("y", "-60%").attr("width", "220%").attr("height", "220%");
        pinShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "2").attr("stdDeviation", "2.4")
          .attr("flood-color", "#0f172a").attr("flood-opacity", "0.22");

        const pillShadow = defs.append("filter")
          .attr("id", "eu-pill-shadow").attr("x", "-30%").attr("y", "-30%").attr("width", "160%").attr("height", "160%");
        pillShadow.append("feDropShadow")
          .attr("dx", "0").attr("dy", "1.5").attr("stdDeviation", "2")
          .attr("flood-color", "#1e293b").attr("flood-opacity", "0.16");

        const glow = defs.append("filter").attr("id", "eu-glow").attr("x", "-60%").attr("y", "-60%").attr("width", "220%").attr("height", "220%");
        glow.append("feGaussianBlur").attr("stdDeviation", "3").attr("result", "blur");
        const gm = glow.append("feMerge");
        gm.append("feMergeNode").attr("in", "blur");
        gm.append("feMergeNode").attr("in", "SourceGraphic");

        // Permanent soft radial vignette for ambient depth across the whole map.
        const vignette = defs.append("radialGradient")
          .attr("id", "eu-vignette").attr("gradientUnits", "userSpaceOnUse")
          .attr("cx", W / 2).attr("cy", H * 0.42).attr("r", W * 0.62);
        vignette.append("stop").attr("offset", "0%").attr("stop-color", "#bfe0fb").attr("stop-opacity", "0.32");
        vignette.append("stop").attr("offset", "55%").attr("stop-color", "#bfe0fb").attr("stop-opacity", "0");
        vignette.append("stop").attr("offset", "100%").attr("stop-color", "#1e3a5f").attr("stop-opacity", "0.14");

        // Ambient flag-colour glow that washes the whole map on hover.
        const ambient = defs.append("radialGradient")
          .attr("id", "eu-ambient").attr("gradientUnits", "userSpaceOnUse")
          .attr("cx", W / 2).attr("cy", H / 2).attr("r", W * 0.55);
        const ambStop0 = ambient.append("stop").attr("offset", "0%").attr("stop-color", "#0284c7").attr("stop-opacity", "0.5");
        const ambStop1 = ambient.append("stop").attr("offset", "65%").attr("stop-color", "#0284c7").attr("stop-opacity", "0");

        // Tone the flag down so it sits in the map's palette (desaturate + soften).
        const flagTone = defs.append("filter").attr("id", "eu-flag-tone");
        flagTone.append("feColorMatrix").attr("type", "saturate").attr("values", "0.78");

        // Glassy sheen overlay laid on top of the flag (light top, dark bottom).
        const sheen = defs.append("linearGradient")
          .attr("id", "eu-flag-sheen").attr("x1", "0").attr("x2", "0").attr("y1", "0").attr("y2", "1");
        sheen.append("stop").attr("offset", "0%").attr("stop-color", "#ffffff").attr("stop-opacity", "0.22");
        sheen.append("stop").attr("offset", "45%").attr("stop-color", "#ffffff").attr("stop-opacity", "0");
        sheen.append("stop").attr("offset", "100%").attr("stop-color", "#0b1f3a").attr("stop-opacity", "0.3");

        // Flag-colour gradient — base layer under the flag image. If the (CDN)
        // flag image is slow or fails, the country still shows its flag colours
        // instead of an empty/dark patch. Stops updated per hovered country.
        const flagGrad = defs.append("linearGradient")
          .attr("id", "eu-flag-grad").attr("x1", "0").attr("x2", "1").attr("y1", "0").attr("y2", "1");
        const flagStop1 = flagGrad.append("stop").attr("offset", "0%").attr("stop-color", "#0284c7");
        const flagStop2 = flagGrad.append("stop").attr("offset", "100%").attr("stop-color", "#0ea5e9");

        // Sea
        svg.append("rect").attr("width", W).attr("height", H).attr("fill", "url(#eu-sea)");

        // Faint guide grid
        const grid = svg.append("g").attr("opacity", 0.45);
        for (let x = 0; x <= W; x += 49) {
          grid.append("line").attr("x1", x).attr("y1", 0).attr("x2", x).attr("y2", H)
            .attr("stroke", "#d3e2f0").attr("stroke-width", 1);
        }
        for (let y = 0; y <= H; y += 49) {
          grid.append("line").attr("x1", 0).attr("y1", y).attr("x2", W).attr("y2", y)
            .attr("stroke", "#d3e2f0").attr("stroke-width", 1);
        }

        // Ambient gradient wash over the whole map (always-on depth).
        svg.append("rect").attr("width", W).attr("height", H)
          .attr("fill", "url(#eu-vignette)").style("pointer-events", "none");

        const proj = d3.geoMercator().center([15.5, 50.5]).scale(850).translate([W / 2, H / 2]);
        const pathGen = d3.geoPath().projection(proj);

        const world = await fetch(WORLD_ATLAS, { signal: ctrl.signal }).then((r) => {
          if (!r.ok) throw new Error("world atlas fetch failed");
          return r.json();
        });
        if (cancelled) return;

        fixCrimeaBorders(world, topojson, d3);
        const features = topojson.feature(world, world.objects.countries).features.filter((f) => EUROPE.has(+f.id));
        // Dissolve the Crimea↔mainland seam so Ukraine renders as one shape.
        const uaGeom = world.objects.countries.geometries.find((g) => +g.id === 804);
        const uaFeat = features.find((f) => +f.id === 804);
        if (uaGeom && uaFeat) uaFeat.geometry = topojson.merge(world, [uaGeom]);

        const gCountries = svg.append("g")
          .attr("class", "eu-map__countries")
          .attr("filter", "url(#eu-land-shadow)");
        gCountries
          .selectAll("path")
          .data(features)
          .join("path")
          .attr("class", (f) => (HIGHLIGHT.has(+f.id) ? "eu-map__country eu-map__country--hi" : "eu-map__country"))
          .attr("d", pathGen)
          .attr("fill", (f) => `url(#${HIGHLIGHT.has(+f.id) ? "eu-land-hi" : "eu-land"})`)
          .attr("stroke", (f) => HIGHLIGHT.has(+f.id) ? "#7cc0ee" : "#cdd9e8")
          .attr("stroke-width", (f) => HIGHLIGHT.has(+f.id) ? 1 : 0.8)
          .attr("stroke-linejoin", "round")
          .attr("pointer-events", (f) => (+f.id === 643 ? "none" : null)) // Russia: not interactive
          .style("cursor", (f) => {
            if (+f.id === 643) return "default";
            const info = EU_COUNTRY[+f.id];
            return info && portsForCountry(info.iso2).length ? "pointer" : "default";
          })
          .on("mouseenter", function onEnter(ev, f) {
            if (+f.id === 643) return;
            const info = EU_COUNTRY[+f.id];
            const hasPorts = info && portsForCountry(info.iso2).length > 0;
            // Countries WITHOUT a port: just show the name on hover, no flag.
            if (!hasPorts) {
              const tt = ttRef.current;
              if (tt && info) {
                tt.classList.remove("us-map__tt--flag");
                tt.innerHTML = `<div class="us-map__tt-title">${info.name}</div>`;
                tt.classList.add("show");
              }
              return;
            }
            // Port country → glow outline + flag fill.
            d3.select(this)
              .attr("stroke", "#ffffff")
              .attr("stroke-width", 1.6)
              .attr("filter", "url(#eu-hover-glow)")
              .raise();
            flagLayer.selectAll("*").remove();
            const dAttr = d3.select(this).attr("d");
            // Union the bounds of every ON-SCREEN polygon (mainland + nearby
            // islands such as Corsica) while ignoring far overseas territories
            // (e.g. French Guiana) that would blow up the bounding box. This way
            // the flag covers the islands too — no orphan dark patches.
            const geom = f.geometry;
            const polys = geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates];
            let X0 = Infinity, Y0 = Infinity, X1 = -Infinity, Y1 = -Infinity;
            for (const poly of polys) {
              const c = pathGen.centroid({ type: "Polygon", coordinates: poly });
              if (!c || !Number.isFinite(c[0]) || c[0] < -20 || c[0] > W + 20 || c[1] < -20 || c[1] > H + 20) continue;
              const bb = pathGen.bounds({ type: "Feature", geometry: { type: "Polygon", coordinates: poly } });
              X0 = Math.min(X0, bb[0][0]); Y0 = Math.min(Y0, bb[0][1]);
              X1 = Math.max(X1, bb[1][0]); Y1 = Math.max(Y1, bb[1][1]);
            }
            if (!Number.isFinite(X0)) return;
            const x = X0, y = Y0, w = X1 - X0, h = Y1 - Y0;
            // Ambient flag-colour wash over the whole map.
            ambient.attr("cx", x + w / 2).attr("cy", y + h / 2);
            ambStop0.attr("stop-color", info.c1);
            ambStop1.attr("stop-color", info.c1);
            ambientRect.attr("opacity", 0.32);
            // Flag-colour gradient base — instant + reliable, so the country
            // always shows its colours even if the CDN flag image is slow/fails
            // (never an empty/dark patch). Mapped to the mainland bbox via a rect.
            flagStop1.attr("stop-color", info.c1);
            flagStop2.attr("stop-color", info.c2);
            flagClipPath.attr("d", dAttr);
            // All hover visuals live in one group so they fade in smoothly (CSS).
            const fx = flagLayer.append("g").attr("class", "eu-map__flag-fx");
            fx.append("rect")
              .attr("x", x).attr("y", y).attr("width", w).attr("height", h)
              .attr("fill", "url(#eu-flag-grad)")
              .attr("clip-path", "url(#eu-flag-clip)")
              .style("pointer-events", "none");
            // Real flag image on top (toned to the map palette).
            const flagUrl = `https://flagcdn.com/w640/${info.iso2.toLowerCase()}.png`;
            fx.append("image")
              .attr("href", flagUrl).attr("xlink:href", flagUrl)
              .attr("x", x).attr("y", y).attr("width", w).attr("height", h)
              .attr("preserveAspectRatio", "none")
              .attr("clip-path", "url(#eu-flag-clip)")
              .attr("filter", "url(#eu-flag-tone)")
              .attr("opacity", 0.92);
            // Glassy sheen overlay (light top → dark bottom) keeps it in-style.
            fx.append("rect")
              .attr("x", x).attr("y", y).attr("width", w).attr("height", h)
              .attr("fill", "url(#eu-flag-sheen)")
              .attr("clip-path", "url(#eu-flag-clip)")
              .style("pointer-events", "none");
            // Country-name pill under the territory.
            const cx = x + w / 2, py = Math.min(y + h + 16, H - 14);
            const tw = info.name.length * 7.2 + 22;
            const pill = fx.append("g").attr("transform", `translate(${cx},${py})`);
            pill.append("rect")
              .attr("x", -tw / 2).attr("y", -12).attr("width", tw).attr("height", 24).attr("rx", 12)
              .attr("fill", "#0f172a").attr("filter", "url(#eu-pin-shadow)");
            pill.append("text")
              .attr("text-anchor", "middle").attr("dy", "0.34em")
              .attr("font-size", "12.5px").attr("font-weight", "800").attr("fill", "#fff")
              .text(info.name);
          })
          .on("mousemove", function onMove(ev) {
            const tt = ttRef.current, wrap = wrapRef.current;
            if (!tt || !wrap || !tt.classList.contains("show")) return;
            const r = wrap.getBoundingClientRect();
            const tw = tt.offsetWidth || 140, th = tt.offsetHeight || 34;
            let x = ev.clientX - r.left + 14, y = ev.clientY - r.top - th / 2;
            if (x + tw > r.width - 8) x = ev.clientX - r.left - tw - 14;
            if (y < 8) y = 8;
            if (y + th > r.height - 8) y = r.height - th - 8;
            tt.style.left = `${x}px`; tt.style.top = `${y}px`;
          })
          .on("mouseleave", function onLeave(ev, f) {
            if (+f.id === 643) return;
            const isHi = HIGHLIGHT.has(+f.id);
            d3.select(this)
              .attr("stroke", isHi ? "#7cc0ee" : "#cdd9e8")
              .attr("stroke-width", isHi ? 1 : 0.8)
              .attr("filter", null);
            flagLayer.selectAll("*").remove();
            ambientRect.attr("opacity", 0);
            const tt = ttRef.current;
            if (tt) tt.classList.remove("show");
          })
          .on("click", function onClick(ev, f) {
            if (+f.id === 643) return;
            const info = EU_COUNTRY[+f.id];
            if (!info) return;
            const cps = portsForCountry(info.iso2);
            if (cps.length === 0) return;            // no ports here → do nothing
            if (cps.length === 1) {                  // single port → select directly
              onSelectPortRef.current?.(cps[0].key);
              chooserOpenRef.current?.(null);
              return;
            }
            const c = pathGen.centroid(f);           // multiple → open chooser
            if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
              chooserOpenRef.current?.({ ports: cps, x: c[0], y: c[1], country: info.name });
            }
          });

        // Hover-only flag-colour wash over the whole map (fades via CSS).
        const ambientRect = svg.append("rect")
          .attr("class", "eu-map__ambient")
          .attr("width", W).attr("height", H)
          .attr("fill", "url(#eu-ambient)").attr("opacity", 0)
          .style("pointer-events", "none");

        // Layer that holds the on-territory flag while hovering.
        const flagLayer = svg.append("g").attr("class", "eu-map__flags").style("pointer-events", "none");

        // ── Animated sea route: arriving from the Atlantic → selected port ──
        if (selectedPort) {
          const sp = ports.find((p) => p.key === selectedPort);
          const ppt = sp ? proj([sp.lng, sp.lat]) : null;
          if (ppt) {
            const start = [-30, H * 0.4]; // off the west edge (incoming from US)
            const dx = ppt[0] - start[0], dy = ppt[1] - start[1];
            const dist = Math.hypot(dx, dy) || 1;
            const mx = (start[0] + ppt[0]) / 2 + (-dy / dist) * dist * 0.14;
            const my = (start[1] + ppt[1]) / 2 + (dx / dist) * dist * 0.14;
            const d = `M ${start[0]} ${start[1]} Q ${mx} ${my} ${ppt[0]} ${ppt[1]}`;
            const routeLayer = svg.append("g").attr("class", "us-map__route-layer");
            routeLayer.append("path").attr("class", "us-map__route-base").attr("d", d);
            routeLayer.append("path").attr("class", "us-map__route-flow").attr("d", d);
          }
        }

        // Port markers + always-on labels
        const portLayer = svg.append("g").attr("class", "eu-map__ports");
        ports.forEach((p) => {
          const pt = proj([p.lng, p.lat]);
          if (!pt) return;
          const isSelected = p.key === selectedPort;

          const g = portLayer.append("g")
            .attr("class", "eu-map__port")
            .attr("transform", `translate(${pt[0]},${pt[1]})`)
            .style("cursor", "pointer")
            .on("click", () => {
              const cb = onSelectPortRef.current;
              if (typeof cb === "function") cb(p.key);
            })
            .on("mouseenter", function onEnter() {
              const tt = ttRef.current;
              if (!tt) return;
              tt.classList.remove("us-map__tt--flag");
              tt.innerHTML = `<div class="us-map__tt-title">${p.label}</div>`;
              tt.classList.add("show");
            })
            .on("mousemove", function onMove(ev) {
              const tt = ttRef.current;
              const wrap = wrapRef.current;
              if (!tt || !wrap) return;
              const r = wrap.getBoundingClientRect();
              const tw = tt.offsetWidth || 160;
              const th = tt.offsetHeight || 40;
              let x = ev.clientX - r.left + 14;
              let y = ev.clientY - r.top - th / 2;
              if (x + tw > r.width - 8) x = ev.clientX - r.left - tw - 14;
              if (y < 8) y = 8;
              if (y + th > r.height - 8) y = r.height - th - 8;
              tt.style.left = `${x}px`;
              tt.style.top = `${y}px`;
            })
            .on("mouseleave", function onLeave() {
              const tt = ttRef.current;
              if (tt) tt.classList.remove("show");
            });

          if (isSelected) {
            g.raise();
            g.append("circle")
              .attr("class", "us-map__pulse")
              .attr("r", 12).attr("fill", ACCENT).attr("fill-opacity", 0.45).attr("filter", "url(#eu-glow)");
          }

          g.append("circle")
            .attr("r", isSelected ? 8 : 6)
            .attr("fill", "#fff")
            .attr("filter", "url(#eu-pin-shadow)");
          g.append("circle")
            .attr("r", isSelected ? 5 : 3.6)
            .attr("fill", isSelected ? ACCENT_DARK : ACCENT);

          drawPill(g, {
            text: p.label, dir: p.dir || "right", active: isSelected,
            dotColor: isSelected ? "#bae6fd" : ACCENT,
          });
        });

        setStatus("ok");
      } catch (e) {
        if (!cancelled) setStatus("err");
      }
    }

    run();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [ports, selectedPort]);

  return (
    <div className="us-map" ref={wrapRef}>
      {status === "loading" && <div className="us-map__loading"><span className="us-map__spinner" />Loading map…</div>}
      {status === "err" && <div className="us-map__error">Map failed to load.</div>}
      <svg ref={svgRef} className="us-map__svg" viewBox={`0 0 ${W} ${H}`} />
      <div ref={ttRef} className="us-map__tt" />
      {chooser && (
        <div
          className="eu-map__chooser"
          style={{ left: `${(chooser.x / W) * 100}%`, top: `${(chooser.y / H) * 100}%` }}
          role="dialog"
        >
          <button type="button" className="eu-map__chooser-close" onClick={() => setChooser(null)} aria-label="Close">×</button>
          <div className="eu-map__chooser-title">{chooser.country}</div>
          <div className="eu-map__chooser-sub">Choose arrival port</div>
          <div className="eu-map__chooser-list">
            {chooser.ports.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`eu-map__chooser-port${p.key === selectedPort ? " is-active" : ""}`}
                onClick={() => { onSelectPort?.(p.key); setChooser(null); }}
              >
                <span className="eu-map__chooser-anchor" aria-hidden="true">⚓</span>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function drawPill(parent, { text, dir = "right", active = false }) {
  const padX = 12;
  const charW = 6.5;
  const tw = Math.round(text.length * charW + padX * 2);
  const th = 22;
  const gap = 13;

  let x = 0, y = 0;
  switch (dir) {
    case "left": x = -(gap + tw / 2); y = 0; break;
    case "top": x = 0; y = -(gap + th / 2); break;
    case "bottom": x = 0; y = gap + th / 2; break;
    case "right":
    default: x = gap + tw / 2; y = 0; break;
  }

  const g = parent.append("g").attr("transform", `translate(${x},${y})`);
  const bg = active ? "#0284c7" : "#ffffff";
  const fg = active ? "#ffffff" : "#0f172a";
  const stroke = active ? "#0284c7" : "#e2e8f0";

  g.append("rect")
    .attr("x", -tw / 2).attr("y", -th / 2)
    .attr("width", tw).attr("height", th).attr("rx", 11)
    .attr("fill", bg).attr("stroke", stroke).attr("stroke-width", 1)
    .attr("filter", "url(#eu-pill-shadow)");
  g.append("text")
    .attr("text-anchor", "middle").attr("x", 0).attr("y", 0).attr("dy", "0.34em")
    .attr("font-size", "11px").attr("font-weight", "700").attr("fill", fg)
    .text(text);
  return g;
}
